import React from 'react'
import PropTypes from 'prop-types'
import styled, { css } from 'styled-components'
import { Field } from 'redux-form'
import Theme from 'components/common/Theme/Theme'
import Mixin from 'components/common/Mixin'
import Media from 'components/common/Media'
import Banner from 'components/common/Banner/Banner'
import { Input } from 'components/common/FormElements/Input'
import { formatPhoneNumber, normalizePhoneNumber } from 'helpers/phone-number'
import { Col } from 'components/AppointmentConfirmationPage/AppointmentInfo/components/AppointmentInfo.styles'
import model from 'components/AppointmentConfirmationPage/AppointmentInfo/viewModel'
import {
  CONTACT_INFORMATION,
  WAIT_DROPOFF_HEADER,
  CUSTOMER_HEADER_ID,
  WAIT_DROPOFF_HEADER_ID
} from 'components/AppointmentConfirmationPage/constants'
import { PHONE_HEADER_ID, EMAIL_HEADER_ID } from 'components/AppointmentConfirmationPage/AppointmentInfo/constants'
import { APPOINTMENT_CONTACT_INFO_TARGET_ID, SUBMIT_ERROR_ID } from './../../constants'
import { KEEP_IN_CONTACT_LABEL } from 'constant'
import { PRIVACY_POLICY_LABEL, PRIVACY_POLICY_LINK, DATEPICKER_THEME_COLOR_GREEN } from 'components/QuickAppointmentPage/ThreeStepper/constants'
import { KeepInContact, TermsAgreement } from 'components/Login/CreateAccount/CreateAccountFormPage/components/fields'
import { Separator } from 'components/common/Separator/Separator'

const CustomInput = styled(Input)`
  & [data-error-message] {
    position: relative;
    top: 4px;
  }
`
const BOTAppointmentContactDetails = ({ handleSubmit, submitFailed, error, isEmailMe }) =>
  <Wrapper id={ APPOINTMENT_CONTACT_INFO_TARGET_ID }>
    <Form noValidate data-at-appointment-info-form onSubmit={ handleSubmit }>
      {
        submitFailed && error &&
          <ErrorBanner id={ submitFailed ? SUBMIT_ERROR_ID : '' } error_alternative>{ error }</ErrorBanner>
      }

      <Headline
        id={ WAIT_DROPOFF_HEADER_ID }
        margin={ '0 0 20px -80px' }
        fontStyle={ 'italic' }
        data-at-wait-dropoff-header>
        Would you like to …
      </Headline>
      <ButtonsWrapper>
        <ButtonsFieldset>
          <legend><Media.ScreenReader>{ WAIT_DROPOFF_HEADER }</Media.ScreenReader></legend>
          <SpanSpacer>
            <RadioInputField { ...model.userPlan.wait } component={ Input } />
          </SpanSpacer>
          <RadioInputField { ...model.userPlan.dropOff } component={ Input } />
        </ButtonsFieldset>
      </ButtonsWrapper>

      <Row>
        <Subtitle headline_narrow >Contact Information</Subtitle>
        <fieldset>
          <legend><Media.ScreenReader>{ CONTACT_INFORMATION }</Media.ScreenReader></legend>
          <Headline id={ CUSTOMER_HEADER_ID } data-at-customer-header>
            To whom should we send your appointment information once confirmed?
          </Headline>
          <CustomCell first>
            <Row>
              <Field { ...model.firstName } small full component={ CustomInput } />
            </Row>
          </CustomCell>
          <CustomCell>
            <Row>
              <Field { ...model.lastName } small full component={ CustomInput } />
            </Row>
          </CustomCell>
          <Headline id={ PHONE_HEADER_ID } margin={ '40px 0 20px' } data-at-phone-header>
            We will contact you by phone to confirm your appointment at ...
          </Headline>
          <Field
            { ...model.phoneNumber }
            component={ Input }
            formatValueDisplay={ formatPhoneNumber }
            normalize={ normalizePhoneNumber }
            full />
          <Headline id={ EMAIL_HEADER_ID } margin={ '40px 0 20px' } data-at-email-header>
            We will email your appointment details to …
          </Headline>
          <Field { ...model.email } component={ Input } full />
        </fieldset>

        <CheckboxesWrapper>
          {!isEmailMe && (
            <>
              <KeepInContact
                form={ model }
                label={ KEEP_IN_CONTACT_LABEL }
                inputColor={ DATEPICKER_THEME_COLOR_GREEN } />
              <Separator margin="23px 0 28px" />
            </>
          )}
          <TermsAgreement
            form={ model }
            path={ PRIVACY_POLICY_LINK }
            linkLabel={ PRIVACY_POLICY_LABEL }
            inputColor={ DATEPICKER_THEME_COLOR_GREEN } />
        </CheckboxesWrapper>
      </Row>
    </Form>
  </Wrapper>

export default BOTAppointmentContactDetails

BOTAppointmentContactDetails.propTypes = {
  error: PropTypes.string,
  handleSubmit: PropTypes.func,
  isEmailMe: PropTypes.bool,
  submitFailed: PropTypes.bool
}

const Wrapper = styled.div`
  ${Mixin.responsive('margin', '0 auto', '0 16px')}
`

const Form = styled.form`
  ${Mixin.responsive('padding', '0 0 40px 0', '0')}
`

const Headline = styled.p`
  clear: both;
  font-style: italic;
  color: ${Theme.colors.dark_grey};
  ${Mixin.font.GothamMediumItalic(14, 600, 22)}
  margin: ${props => props.margin || '0 0 20px'};
  ${Media.mobile`
    padding: 0;
    margin:  0 0 20px;
  `}
`

const Subtitle = styled.h3`
  margin-top: 70px;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  font-stretch: condensed;
  ${Mixin.font.GothamBold(20)}
  transform: scale(1, 1.3);
`

const Row = styled.div`
  ${Mixin.responsive('margin', '0 -12px', '0')}
  ${Mixin.clearFix()}
`

const ButtonsWrapper = styled.div`
  margin: 0 0 40px -80px;
  ${Media.mobile`
    margin:  0 0 40px;
  `}
`

const ButtonsFieldset = styled.fieldset`
  display: flex;
  ${Media.mobile`
    margin:  0 0 40px;
    flex-direction: column;
  `}

  & label {
    display: flex;
    flex-direction: column;
    max-width: 250px;
    margin: 0;
    padding: 0 25px 20px 35px;
    ${Media.mobile`
      padding: 25px 25px 20px 35px;
    `}
  }
`

const ErrorBanner = styled(Banner)`
  padding: 0 30px 18px;
`
const SpanSpacer = styled.span`
  position: relative;
  ${Mixin.responsive('margin-right', '32px', '16px')}

  &::after {
    position: absolute;
    right: 0;
    top: 0;
    bottom: 0;
    width: 2px;
    height: 100%;
    content: '';
    background: ${Theme.colors.light_grey};
    ${Media.mobile`
      right: 0;
      left: 0;
      bottom: 0;
      top: unset;
      width: 100%;
      height: 2px;
    `}
  }
`

const CustomCell = styled(Col)`
  ${props => props.first && css`
    margin-right: 30px;
  `}
`

const RadioInputField = styled(Field)`
  padding: 7px 10px 7px 32px;
  border-radius: 5px;

  input:checked + .radio-input-text {
    &::after {
      background: ${Theme.colors.white};
      left: 16px;
      top: 10px;
      ${Media.mobile`
        top: 35px;
      `}
    }

    &::before {
      background: ${Theme.colors.green};
      left: 7px;
      border-color: ${Theme.colors.green};
      ${Media.mobile`
        top: 35px;
      `}
    }
  }

  input + .radio-input-text {
    ${Mixin.font.GothamMedium(20)}

    &::before {
      left: 5px;
      top: 10px;
      border-width: 2px;
      ${Media.mobile`
        top: 35px;
      `}
    }
  }

  input:checked + label {
    border-color: ${Theme.colors.green};
  }
`

const CheckboxesWrapper = styled.div`
  max-width: 490px;
  margin: 60px 0 80px;
`
